import { TourService } from '@app/services/tour-service/tour.service';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
  effect,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MenuItem, MenuItemCommandEvent } from 'primeng/api';
import { Router } from '@angular/router';
import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { AuthStore, AuthStoreType, ThemeStore } from '@app/stores';
import { ToastService } from '@app/services/toast.service';
import { NotificacionesService } from '@app/services/notificaciones.service';
import { CampanaComponent } from '@app/componentes/comunes/campana/campana.component';

@Component({
  selector: 'app-barra-superior',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './barra-superior.component.html',
  styleUrls: ['./barra-superior.component.scss'],
  imports: [MenubarModule, ButtonModule, TooltipModule, ProgressBarModule, CampanaComponent],
})
export class BarraSuperiorComponent implements OnInit {
  @ViewChild('barrasup', { static: false }) barrasup!: ElementRef;

  items = signal<MenuItem[]>([]);

  private toast: ToastService = inject(ToastService);
  public router: Router = inject(Router);
  private tourService: TourService = inject(TourService);
  authStore: AuthStoreType = inject(AuthStore);
  readonly themeStore: InstanceType<typeof ThemeStore> = inject(ThemeStore); // Para usar en template
  readonly notifService = inject(NotificacionesService);

  constructor() {
    // Effect para actualizar el menú y notificaciones cuando el usuario se autentica
    effect(() => {
      const user = this.authStore.user();
      if (user) {
        this.iniciarItemsMenu();
        this.notifService.init();
      }
    });
  }

  ngOnInit() {
    this.iniciarItemsMenu();
  }

  cambioUrlSpinner(evento: MenuItemCommandEvent) {
    if (!evento.item) return;
    const elementoActual = this.router.url.slice(1, this.router.url.length);
    if (!evento.item?.['routerLink']?.includes(elementoActual)) {
      this.authStore.showLoading();
    }
  }

  iniciarItemsMenu() {
    const currentUser = this.authStore.user();
    const esPadre = this.authStore.esPadre();
    const esFree = this.authStore.esFree();

    // Subitems de "Mis Datos" — algunos visibles solo para el usuario principal
    const misDatosItems: MenuItem[] = [
      {
        label: 'Perfil',
        icon: 'pi pi-user',
        routerLink: ['perfil'],
        title: 'Datos de su perfil.',
        command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
      },
    ];

    if (esPadre) {
      misDatosItems.push(
        {
          label: 'Configuración',
          icon: 'pi pi-cog',
          routerLink: ['configuracion'],
          title: 'Configuración y personalización del panel de control.',
          command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
        },
        ...(!esFree
          ? [
              {
                label: 'Facturas',
                icon: 'pi pi-file',
                routerLink: ['facturas'],
                title: 'Lista de sus facturas.',
                command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
              },
            ]
          : []),
        {
          label: 'Tokens',
          icon: 'pi pi-id-card',
          routerLink: ['tokens'],
          title: 'Administra los tokens para usar con la API REST.',
          command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
        },
        {
          separator: true,
        },
        {
          label: 'Usuarios Secundarios',
          icon: 'pi pi-users',
          routerLink: ['subusuarios'],
          title: 'Gestiona los usuarios secundarios de tu cuenta.',
          command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
        }
      );
    }

    // Subitems de "Más" — crédito y recarga solo para el usuario principal
    const masItems: MenuItem[] = [];

    if (esPadre) {
      masItems.push(
        ...(!esFree
          ? [
              {
                marcador: 'CREDITO',
                label: this.authStore.user()?.credito_disponible?.toFixed(2) + '€' || '0.00€',
                icon: 'pi pi-check',
                title: 'Crédito disponible en este momento.',
              },
              {
                label: 'Recargar',
                icon: 'pi pi-money-bill',
                routerLink: ['stripe'],
                title: 'Recargar créditos.',
                command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
              },
              {
                separator: true,
              },
            ]
          : [])
      );
    }

    masItems.push(
      {
        label: 'Notificaciones',
        icon: 'pi pi-bell',
        routerLink: ['notificaciones'],
        title: 'Ver todas las notificaciones.',
        command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
      },
      {
        label: 'Manual',
        icon: 'pi pi-info-circle',
        title: 'Ver el manual de ayuda online.',
        command: (_event: MenuItemCommandEvent) => {
          const url = 'https://certifirm.eu/web/guia-del-panel-de-control/';
          window.open(url, '_blank');
        },
      },
      {
        label: 'Soporte',
        icon: 'pi pi-ticket',
        routerLink: ['soporte'],
        title: 'Formulario de contacto.',
        command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
      },
      {
        label: 'API',
        icon: 'pi pi-code',
        routerLink: ['api'],
        title: 'Documentación de la API REST.',
        command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
      },
      {
        separator: true,
      },
      {
        label: 'Salir',
        icon: 'pi pi-sign-out',
        // Sin routerLink: authStore.logout() ya navega a /login en su finally block.
        // Tenerlo aquí a la vez provocaba una race condition: LoginComponent.ngOnInit()
        // hacía ping() antes de que finalizara el POST /logout, detectaba sesión activa
        // y redirigía de vuelta a /inicio → el usuario tenía que desloguearse dos veces.
        command: (_event: MenuItemCommandEvent) => {
          this.logout();
        },
      }
    );

    const menuItems = [
      {
        label: 'Inicio',
        icon: 'pi pi-home',
        routerLink: ['inicio'],
        title: 'Resumen de las funciones de la aplicación.',
        command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
      },
      {
        label: 'Mis Datos',
        icon: 'pi pi-list',
        title: 'Consulta los datos de tu perfil.',
        items: misDatosItems,
      },

      // ENTREGA CERTIFICADA
      {
        label: 'Firma Electronica',
        icon: 'pi pi-file-pdf',
        title: 'Entrega Certificada y Firma Electrónicia.',
        items: [
          {
            label: 'Contactos',
            icon: 'pi pi-users',
            routerLink: ['contactos'],
            title: 'Crea, modifica y añade contactos.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Plantillas',
            icon: 'pi pi-file',
            routerLink: ['plantillas-listar'],
            title: 'Crear, editar y modificar sus plantillas de envío de documentos.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Nuevo Envío',
            icon: 'pi pi-envelope',
            routerLink: ['envios-nuevo'],
            title: 'Enviar un documento para firmar.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Consultar Envíos',
            icon: 'pi pi-list',
            routerLink: ['envios-consultar'],
            title: 'Consulta los envíos realizados.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
        ],
      },

      // SELLO DE TIEMPO
      {
        label: 'Sellos de Tiempo',
        icon: 'pi pi-clock',
        title: 'Sello de tiempo para archivos digitales',
        items: [
          {
            label: 'Navegación Certificada',
            icon: 'pi pi-globe',
            routerLink: ['navegacion-certificada'],
            title: 'Inicia una sesión de navegación web grabada y certificada.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Sellar/Certificar Documento',
            icon: 'pi pi-file-pdf',
            routerLink: ['sellar-documento'],
            title: 'Certifica un fichero digital.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },

          {
            label: 'Consultar Sellos/Certificados',
            icon: 'pi pi-list',
            routerLink: ['sellos-de-tiempo-consultar'],
            title: 'Consulta los sellos/certificados de tiempo solicitados.',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
        ],
      },

      // MÁS OPCIONES
      {
        label: 'Más',
        icon: 'pi pi-plus',
        title: 'Más opciones',
        items: masItems,
      },
    ];

    // Agregar menú de administrador si es superadmin
    if (currentUser?.isSuperAdmin) {
      menuItems.push({
        label: '',
        icon: 'pi pi-star',
        title: 'Administración',
        items: [
          {
            label: 'Lista de usuarios',
            icon: 'pi pi-users',
            routerLink: ['administracion-usuarios'],
            title: 'Lista de usuarios',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Facturas',
            icon: 'pi pi-file-pdf',
            routerLink: ['administracion-facturas'],
            title: 'Lista de facturas',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Envíos',
            icon: 'pi pi-send',
            routerLink: ['administracion-envios'],
            title: 'Todos los envíos del sistema',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Sellos de tiempo',
            icon: 'pi pi-clock',
            routerLink: ['administracion-sellos-de-tiempo'],
            title: 'Todos los sellos de tiempo del sistema',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Estadísticas',
            icon: 'pi pi-chart-bar',
            routerLink: ['administracion-stats'],
            title: 'Estadísticas de uso',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Errores',
            icon: 'pi pi-exclamation-triangle',
            routerLink: ['administracion-errores'],
            title: 'Errores registrados del sistema',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
          {
            label: 'Soporte',
            icon: 'pi pi-comments',
            routerLink: ['administracion-soporte'],
            title: 'Mensajes de soporte enviados por los usuarios',
            command: (e: MenuItemCommandEvent) => this.cambioUrlSpinner(e),
          },
        ],
      });
    }

    this.items.set(menuItems);
  }

  logout() {
    this.authStore.logout();
    this.toast.info('Te has deslogueado correctamente.', 'Sesión cerrada');
  }

  btnAyuda() {
    this.tourService.mostrarTour();
  }

  // Métodos para el selector de tema
  cycleTheme() {
    const currentPreference = this.themeStore.userPreference();

    // Ciclo: system -> light -> dark -> system
    switch (currentPreference) {
      case 'system':
        // Cambiar a light y guardar en backend
        this.authStore.changeTheme('light');
        break;
      case 'light':
        // Cambiar a dark y guardar en backend
        this.authStore.changeTheme('dark');
        break;
      case 'dark':
        // Volver a system y guardar en backend
        this.authStore.changeTheme('system');
        break;
    }
  }

  getThemeButtonIcon(): string {
    const preference = this.themeStore.userPreference();

    switch (preference) {
      case 'light':
        return 'pi pi-sun';
      case 'dark':
        return 'pi pi-moon';
      case 'system':
      default:
        return 'pi pi-desktop';
    }
  }

  getThemeButtonSeverity():
    | 'success'
    | 'info'
    | 'warn'
    | 'danger'
    | 'help'
    | 'primary'
    | 'secondary'
    | 'contrast' {
    const preference = this.themeStore.userPreference();

    switch (preference) {
      case 'light':
        return 'warn'; // Botón amarillo (como el sol)
      case 'dark':
        return 'secondary'; // Botón azul oscuro (como la noche)
      case 'system':
      default:
        return 'info'; // Botón azul claro (neutral)
    }
  }

  getThemeButtonTooltip(): string {
    const preference = this.themeStore.userPreference();

    switch (preference) {
      case 'light':
        return 'Tema claro activo. Click para cambiar a tema oscuro.';
      case 'dark':
        return 'Tema oscuro activo. Click para seguir configuración del sistema.';
      case 'system':
      default:
        return 'Siguiendo configuración del sistema. Click para forzar tema claro.';
    }
  }
}
